import * as RadixTabs from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  useContext,
} from 'react';
import { cn } from '../lib/utils.js';

export const Tabs = RadixTabs.Root;

/* Variants de Tabs via cva (ADR-0118) — antes cada call site hand-rolava
   className de underline/pill, gerando inconsistência visual entre níveis de
   navegação. `pill` é o switcher primário (default); `underline` e `secondary`
   são para tabs subordinadas (ex.: Colunas/Relações dentro de Schema), que
   devem parecer claramente distintas do switcher primário. */
const tabsListVariants = cva('inline-flex items-center', {
  variants: {
    variant: {
      pill: 'h-10 gap-1 rounded-md border border-border bg-muted p-1',
      underline: 'h-9 w-full gap-0 rounded-none border-b border-border bg-transparent p-0',
      secondary: 'h-9 w-fit gap-1 rounded-md border border-border bg-muted p-1',
    },
  },
  defaultVariants: { variant: 'pill' },
});

const tabsTriggerVariants = cva(
  'inline-flex items-center justify-center font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        pill: 'rounded-sm px-3 py-1.5 text-sm hover:bg-accent/15 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground',
        underline:
          'flex-1 rounded-none border-b-2 border-transparent px-3 py-1.5 text-sm data-[state=active]:border-primary data-[state=active]:text-foreground',
        secondary:
          'rounded-sm px-3 py-1 text-sm hover:bg-accent/10 data-[state=active]:border data-[state=active]:border-border data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      },
    },
    defaultVariants: { variant: 'pill' },
  },
);

type TabsVariant = NonNullable<VariantProps<typeof tabsListVariants>['variant']>;

/* Context para o `variant` fluir do TabsList aos TabsTrigger sem repetir a prop
   em cada trigger no call site. */
const TabsVariantContext = createContext<TabsVariant>('pill');

export const TabsList = forwardRef<
  ElementRef<typeof RadixTabs.List>,
  ComponentPropsWithoutRef<typeof RadixTabs.List> & VariantProps<typeof tabsListVariants>
>(({ className, variant, ...props }, ref) => {
  const resolved: TabsVariant = variant ?? 'pill';
  return (
    <TabsVariantContext.Provider value={resolved}>
      <RadixTabs.List
        ref={ref}
        className={cn(tabsListVariants({ variant: resolved }), className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
});
TabsList.displayName = 'TabsList';

export const TabsTrigger = forwardRef<
  ElementRef<typeof RadixTabs.Trigger>,
  ComponentPropsWithoutRef<typeof RadixTabs.Trigger> & VariantProps<typeof tabsTriggerVariants>
>(({ className, variant, ...props }, ref) => {
  const inherited = useContext(TabsVariantContext);
  return (
    <RadixTabs.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant: variant ?? inherited }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = 'TabsTrigger';

export const TabsContent = forwardRef<
  ElementRef<typeof RadixTabs.Content>,
  ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(({ className, ...props }, ref) => (
  <RadixTabs.Content
    ref={ref}
    className={cn(
      'mt-2 rounded-md text-foreground focus-visible:outline-none',
      'data-[state=active]:animate-in data-[state=active]:fade-in-0 duration-[150ms] ease-out',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = 'TabsContent';

export { tabsListVariants, tabsTriggerVariants };
